import {Absence, AbsenceType, Shift, Task, TeamMember} from '../../entities';

export type ScheduleCalendarType = 'day' | 'week' | 'month';

export type DatePeriod = {
  fromDate: string;
  toDate: string;
};

export type DateTimePeriod = {
  fromDateTime: string;
  toDateTime: string;
};

export const PeriodPaddingMinutes: {[key in ScheduleCalendarType]: number} = {
  day: 2 * 60,
  week: 24 * 60, // 1 day
  month: 24 * 60, // 1 day
};

export type ScheduleDataModel = {
  members: TeamMember[];
  shiftsWithTasks: ShiftWithTasks[];
  unassignedTasks: Task[];
  absences: AbsenceInfo[];
};

export type ScheduleEditOperations = {
  /**
   * Edit state
   */
  editState: ScheduleEditState;
  /**
   * Tasks that have been dropped in other position/othe shift or as unassigned
   */
  droppedTasks: TaskWithEditInfo[];
  // Edit operations
  cancelEdit: () => void;
  commitEdit: () => Promise<any>;
  /**
   * When starting to drag a task
   */
  beginDragTask: (taskId: number) => void;
  /**
   * The current drag operation was cancelled. Should also
   * cancel edit state if the edit state is equal to the state
   */
  cancelDragTask: (taskId: number) => void;
  /**
   * When shift receives a begin hover task
   */
  beginHoverTaskOverShift: (
    taskId: number,
    shiftId: number,
    sequenceNum: number,
  ) => void;
  /**
   * When shift receives a hover task
   */
  hoverTaskOverShift: (
    taskId: number,
    shiftId: number,
    sequenceNum: number,
  ) => void;
  /**
   * When shift receives an end hover task (task is no longer in shift)
   */
  endHoverTaskOverShift: (taskId: number, shiftId: number) => void;
  /**
   * A task is dropped in a shift
   */
  dropTaskOnShift: (
    taskId: number,
    shiftId: number,
    sequenceNum: number,
  ) => void;
  /**
   * When unassigned receives a begin hover task
   */
  beginHoverTaskOverUnassigned: (taskId: number) => void;
  /**
   * When unassigned receives an end hover task (task is no longer in unassigned)
   */
  endHoverTaskOverUnassigned: (taskId: number) => void;
  /**
   * A task is dropped in unassigned
   */
  dropTaskOnUnassigned: (taskId: number) => void;
};

export type ScheduleDataContextType = ScheduleDataModel &
  ScheduleEditOperations & {
    teamId: number;
    date: Date;
    calendarType: ScheduleCalendarType;
    dates: {
      totalRange: {
        fromDate: string;
        toDate: string;
      };
    };
  };

export type EditMode = 'dragging' | 'hovering' | 'dropped';
export type TaskWithEditInfo = Task & {
  editMode?: EditMode;
};

export type EditedTasks = {[taskId: number]: TaskWithEditInfo};

export type ScheduleEditState = {
  /**
   * Drag & drop edit state
   * Invariant:
   * - currentTaskEdit === undefined => not editing any task
   *   - currentPlaceholders: [] => no placeholders
   * - currentTaskEdit !== undefined => task being edited
   *   - currentPlaceholders: [source] => hovering task within same shift/unassigned or outside any drop target
   *   - currentPlaceholders: [source, target] => hovering task within other shift/unassigned
   * - editedTasks: map of tasks that have been edited. No edit history, just last edit value
   */
  currentTaskEdit?: TaskWithEditInfo | undefined;
  currentPlaceholders: TaskWithEditInfo[];
  editedTasks: EditedTasks;
};

export const InitialScheduleEditState: ScheduleEditState = {
  currentTaskEdit: undefined,
  currentPlaceholders: [],
  editedTasks: {},
};

export type TasksWithDuration = {
  tasks: TaskWithEditInfo[];
  totalTasksHours: number;
};

export type ShiftWithTasks = {
  shift: Shift;
  active: boolean;
} & TasksWithDuration;

export type AbsenceInfo = {
  absence: Absence;
  absenceType: AbsenceType;
};

export type DailyWorkload = {
  totalCapacityHours: number;
  totalAssignedTaskHours: number;
  totalUnassignedTaskHours: number;
};
